import { Event } from 'src/util/events/interfaces/Event';
import { IFSSocket } from 'src/util/socket/IFSSocket';

class MessageBus {
	private ifsSocket: IFSSocket;
	private subscribedListeners: Map<string, { (event: Event): void }[]> = new Map<
		string,
		{ (event: Event): void }[]
	>();

	constructor(ifsSocket: IFSSocket) {
		this.ifsSocket = ifsSocket;
		this.initialize();
	}

	private initialize(): void {
		this.ifsSocket.connect();
		this.ifsSocket.setPublishCallback((event: Event) => this.internalPublish(event));
	}

	private internalPublish(event: Event): void {
		if (!this.subscribedListeners.has(event.eventType)) {
			return;
		}

		const callbacksForTheEvent = this.subscribedListeners.get(event.eventType);
		if (callbacksForTheEvent !== undefined) {
			callbacksForTheEvent.forEach((callback) => {
				callback(event);
			});
		}
	}

	private internalSubscribe(event: Event, callback: (event: Event) => void): void {
		if (!this.subscribedListeners.has(event.eventType)) {
			this.subscribedListeners.set(event.eventType, []);
		}

		const callbacksForTheEvent = this.subscribedListeners.get(event.eventType);
		if (callbacksForTheEvent !== undefined) {
			callbacksForTheEvent.push(callback);
			this.subscribedListeners.set(event.eventType, callbacksForTheEvent);
		}
	}

	private internalUnsubscribe(event: Event, callback: (event: Event) => void): void {
		if (!this.subscribedListeners.has(event.eventType)) {
			return;
		}

		let subscribedListeners = this.subscribedListeners.get(event.eventType) as {
			(event: Event): void;
		}[];
		subscribedListeners = subscribedListeners.filter((callbackFromSubscriber) => {
			return callbackFromSubscriber !== callback;
		});

		this.subscribedListeners.set(event.eventType, subscribedListeners);
	}

	public publish(event: Event): void {
		if (event.isLocal) {
			this.internalPublish(event);
		}

		try {
			this.ifsSocket.publish(event);
		} catch (e) {
			console.error(e);
		}
	}

	public subscribe(event: Event, callback: (event: Event) => void): void {
		this.internalSubscribe(event, callback);
		this.ifsSocket.subscribe(event);
	}

	public unsubscribe(event: Event, callback: (event: Event) => void): void {
		const subscribedListeners = this.subscribedListeners.get(event.eventType);
		if (subscribedListeners !== undefined) {
			if (this.subscribedListeners.has(event.eventType) && subscribedListeners.length === 1) {
				this.ifsSocket.unsubscribe(event);
			}

			return this.internalUnsubscribe(event, callback);
		}
	}

	public dispose(): void {
		this.subscribedListeners.clear();
		this.ifsSocket.dispose();
	}
}

export { MessageBus };
